import { useState, useCallback, useMemo, useEffect } from "react";
import { DISPLAY } from "../constants.js";
import { advancedTimelineDuration } from "../lib/display/runtime.js";
import { updateAnimationSegment } from "./ComposeAnimation.js";
import "./ComposeTimeline.css";

// Rooted in the app's own established brand color (--color-accent, Stack
// Digital royal blue) rather than an arbitrary, generic rotation - a
// narrow, cool-toned/analogous range (blue -> teal -> violet -> slate)
// reads as deliberately curated rather than mirroring Bannerflow's own,
// unrelated palette. Ordered so the two blues (closest to each other in
// hue) are separated by three other colors, rather than adjacent, since
// two visually-similar rows defeats the point of per-row color coding -
// confirmed by direct visual comparison against a naive ordering.
const ROW_COLORS = ["#2450ff", "#0ea5a0", "#6d28d9", "#334155", "#1b3fe0", "#64748b"];

/**
 * Computes the drawable segments for one layer - {type, start, duration,
 * label}[], plus loop metadata for sequence layers. Deliberately mirrors
 * the actual timing model in runtime.js exactly (sampleSequenceFrame's
 * frame.in.delay/frame.out.start are relative to the start of the whole
 * cycle, not the previous frame - same convention used here) rather than
 * inventing a parallel one, so what's drawn always matches what actually
 * plays.
 *
 * Loop visualization: a finite sequence (loops > 0) tiles every segment
 * across each full repetition, up to MAX_LOOPS (matching the same cap
 * runtime.js's own finiteLoops enforces) - every repetition is drawn.
 * An infinite sequence (loops <= 0) only ever draws one cycle's worth;
 * the caller draws a continuation mark after it rather than attempting
 * to draw something unbounded.
 */
function getLayerSegments(layer) {
  if (layer.sequence) {
    const seq = layer.sequence;
    const cycle = Math.max(0.1, seq.cycleDuration);
    const isInfinite = seq.loops <= 0;
    const repeatCount = isInfinite ? 1 : Math.max(1, Math.min(Math.round(seq.loops), DISPLAY.PROFILE.MAX_LOOPS));
    const segments = [];
    for (let rep = 0; rep < repeatCount; rep++) {
      const offset = rep * cycle;
      seq.frames.forEach((frame, i) => {
        const isLast = i === seq.frames.length - 1;
        if (frame.in) {
          segments.push({ type: "in", start: offset + frame.in.delay, duration: frame.in.duration, label: `${frame.in.effect} in` });
        }
        if (frame.emphasis) {
          const times = Math.max(1, frame.emphasis.times || 1);
          segments.push({
            type: "emphasis",
            start: offset + frame.emphasis.delay,
            duration: frame.emphasis.duration * times,
            label: frame.emphasis.effect,
          });
        }
        if (frame.out && !isLast) {
          segments.push({ type: "out", start: offset + frame.out.start, duration: frame.out.duration, label: `${frame.out.effect} out` });
        }
      });
    }
    return { segments, isInfinite, cycleDuration: cycle, kind: "sequence" };
  }

  const a = layer.animation;
  if (!a) return { segments: [], isInfinite: false, kind: "static" };

  const segments = [];
  if (a.in) segments.push({ type: "in", start: a.in.delay, duration: a.in.duration, label: `${a.in.effect} in` });
  if (a.emphasis) {
    const times = Math.max(1, a.emphasis.times || 1);
    const label = a.emphasis.effect + (times > 1 ? ` ×${times}` : "");
    segments.push({ type: "emphasis", start: a.emphasis.delay, duration: a.emphasis.duration * times, label });
  }
  if (a.out) segments.push({ type: "out", start: a.out.start, duration: a.out.duration, label: `${a.out.effect} out` });
  return { segments, isInfinite: false, kind: "animated" };
}

/** Tick spacing chosen so a short timeline doesn't get overcrowded and a
 * long one doesn't get too sparse - roughly 6-10 ticks regardless of
 * overall duration. */
function tickStepFor(maxTime) {
  if (maxTime <= 3) return 0.5;
  if (maxTime <= 8) return 1;
  return 2;
}

const MIN_SEGMENT_DURATION = 0.1;

function snapTo(value, step) {
  return Math.round(value / step) * step;
}
function round2(v) {
  return Math.round(v * 100) / 100;
}

// "in"/"emphasis" use `delay` for their start-time field; "out" uses
// `start` - matches runtime.js's own animation shape exactly.
const startFieldFor = (type) => (type === "out" ? "start" : "delay");

/**
 * Computes the live, snapped {start, duration} for an in-progress drag,
 * given the pixel delta since drag-start. Three modes, matching exactly
 * what was verified in isolation before writing this:
 *  - move: shifts start, duration fixed
 *  - resize-right: extends/shrinks duration, start fixed
 *  - resize-left: start moves, duration adjusts to keep the segment's
 *    own end time fixed
 * All three snap to the same interval the ruler's own ticks use
 * (tickStepFor(maxTime)) and clamp to sensible bounds (never negative,
 * never below MIN_SEGMENT_DURATION, never past the hard animation-length
 * ceiling).
 */
function computeDragPreview(drag, maxTime, hardMax) {
  const { mode, originalStart, originalDuration, currentDx, trackWidthPx } = drag;
  const dxSeconds = (currentDx / trackWidthPx) * maxTime;
  const step = tickStepFor(maxTime);

  if (mode === "move") {
    let start = snapTo(originalStart + dxSeconds, step);
    start = Math.max(0, Math.min(hardMax - originalDuration, start));
    return { start: round2(start), duration: round2(originalDuration) };
  }
  if (mode === "resize-right") {
    let duration = snapTo(originalDuration + dxSeconds, step);
    duration = Math.max(MIN_SEGMENT_DURATION, Math.min(hardMax - originalStart, duration));
    return { start: round2(originalStart), duration: round2(duration) };
  }
  // resize-left
  const segmentEnd = originalStart + originalDuration;
  let start = snapTo(originalStart + dxSeconds, step);
  start = Math.max(0, Math.min(segmentEnd - MIN_SEGMENT_DURATION, start));
  return { start: round2(start), duration: round2(segmentEnd - start) };
}

export default function ComposeTimeline({ doc, selectedLayerId, onSelectLayer, onScrub, onUpdateLayer, editable }) {
  const [t, setT] = useState(0);
  const [scrubbing, setScrubbing] = useState(false);
  const [drag, setDrag] = useState(null);

  const layers = doc?.layers ?? [];

  // Dynamic-fit to the actual content, capped at the real, enforced
  // ceiling on total banner animation length - matches the exact
  // clamp GIF export itself already applies, so the ruler never
  // implies runway that wouldn't actually play or export.
  const maxTime = useMemo(() => {
    const raw = advancedTimelineDuration(doc ?? {});
    return Math.min(Math.max(1, Math.ceil(raw * 10) / 10), DISPLAY.SOCIAL.MAX_SECONDS);
  }, [doc]);

  const rows = useMemo(
    () => layers.map((layer, i) => ({ layer, color: ROW_COLORS[i % ROW_COLORS.length], ...getLayerSegments(layer) })),
    [layers]
  );

  const step = tickStepFor(maxTime);
  const ticks = useMemo(() => {
    const arr = [];
    for (let s = 0; s <= maxTime + 0.001; s += step) arr.push(Math.round(s * 100) / 100);
    return arr;
  }, [maxTime, step]);

  const forEachAnim = useCallback((fn) => {
    document.querySelectorAll('iframe[title^="Preview "]').forEach((ifr) => {
      const d = ifr.contentDocument;
      if (!d || typeof d.getAnimations !== "function") return;
      d.getAnimations().forEach(fn);
    });
  }, []);

  const scrubTo = useCallback(
    (secs) => {
      setT(secs);
      setScrubbing(true);
      onScrub?.(true);
      forEachAnim((a) => {
        a.pause();
        a.currentTime = secs * 1000;
      });
    },
    [forEachAnim, onScrub]
  );

  const play = useCallback(() => {
    setScrubbing(false);
    onScrub?.(false);
    setT(0);
    forEachAnim((a) => {
      a.currentTime = 0;
      a.play();
    });
  }, [forEachAnim, onScrub]);

  function handleRulerClick(e) {
    const rect = e.currentTarget.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    scrubTo(Math.round(frac * maxTime * 20) / 20);
  }

  function startDrag(e, layerId, segType, mode, originalStart, originalDuration) {
    if (!editable) return;
    e.preventDefault();
    e.stopPropagation(); // don't also trigger the row's own onClick handler below
    onSelectLayer?.(layerId); // ...but still select the layer, same as clicking the row would
    const track = e.currentTarget.closest(".compose-timeline__track");
    if (!track) return;
    const trackWidthPx = track.getBoundingClientRect().width;
    setDrag({ layerId, segType, mode, originalStart, originalDuration, pointerStartX: e.clientX, trackWidthPx, currentDx: 0 });
  }

  useEffect(() => {
    if (!drag) return;
    function handleMove(e) {
      setDrag((d) => (d ? { ...d, currentDx: e.clientX - d.pointerStartX } : d));
    }
    function handleUp() {
      setDrag((d) => {
        if (d) commitDrag(d);
        return null;
      });
    }
    window.addEventListener("mousemove", handleMove);
    window.addEventListener("mouseup", handleUp);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      window.removeEventListener("mouseup", handleUp);
    };
    // commitDrag/maxTime intentionally read fresh via closure each time
    // this effect (re-)runs, which happens on every drag start/end -
    // not on every currentDx update, since only `drag`'s identity
    // (not its currentDx field) is a dependency change we want to react to here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drag?.layerId, drag?.segType, drag?.mode]);

  function commitDrag(d) {
    const preview = computeDragPreview(d, maxTime, DISPLAY.PROFILE.MAX_ANIMATION_S);
    const startField = startFieldFor(d.segType);
    onUpdateLayer?.(d.layerId, (l) => {
      if (d.segType === "emphasis") {
        // The bar's displayed duration is duration*times (the sequence
        // frame/layer's total emphasis span); only the per-repeat
        // duration is actually stored, so divide back by the existing,
        // drag-unchanged times count before writing it.
        const times = Math.max(1, l.animation?.emphasis?.times || 1);
        return updateAnimationSegment(l, "emphasis", {
          [startField]: preview.start,
          duration: round2(preview.duration / times),
        });
      }
      return updateAnimationSegment(l, d.segType, { [startField]: preview.start, duration: preview.duration });
    });
  }

  if (layers.length === 0) return null;

  return (
    <div className="compose-timeline">
      <div className="compose-timeline__header">
        <span className="compose-timeline__label">Timeline</span>
        <button type="button" onClick={play} title="Play from the start" className="compose-timeline__play">
          ▶ Play
        </button>
        <span className="compose-timeline__time">
          {(scrubbing ? t : 0).toFixed(2)}s / {maxTime.toFixed(1)}s
        </span>
      </div>

      <div className="compose-timeline__scroll">
        <div className="compose-timeline__grid" style={{ "--row-label-w": "8rem" }}>
          <div className="compose-timeline__ruler-row">
            <div className="compose-timeline__row-label" />
            <div className="compose-timeline__ruler" onClick={handleRulerClick}>
              {ticks.map((tick) => (
                <span key={tick} className="compose-timeline__tick" style={{ left: `${(tick / maxTime) * 100}%` }}>
                  <span className="compose-timeline__tick-line" />
                  <span className="compose-timeline__tick-label">{tick}s</span>
                </span>
              ))}
              <div className="compose-timeline__playhead" style={{ left: `${(Math.min(t, maxTime) / maxTime) * 100}%` }} />
            </div>
          </div>

          {rows.map(({ layer, color, segments, isInfinite, cycleDuration, kind }) => (
            <div
              key={layer.id}
              className={`compose-timeline__track-row${layer.id === selectedLayerId ? " compose-timeline__track-row--selected" : ""}`}
              onClick={() => onSelectLayer?.(layer.id)}
            >
              <span className="compose-timeline__row-label" title={layer.name ?? layer.role}>
                {layer.name ?? layer.role}
              </span>
              <div className="compose-timeline__track">
                <div className="compose-timeline__playhead compose-timeline__playhead--row" style={{ left: `${(Math.min(t, maxTime) / maxTime) * 100}%` }} />
                {kind === "static" ? (
                  <div className="compose-timeline__bar compose-timeline__bar--static" style={{ background: color, opacity: 0.35 }} />
                ) : (
                  segments.map((seg, i) => {
                    const isDraggingThis =
                      drag && kind === "animated" && drag.layerId === layer.id && drag.segType === seg.type;
                    const display = isDraggingThis ? computeDragPreview(drag, maxTime, DISPLAY.PROFILE.MAX_ANIMATION_S) : seg;
                    const draggable = kind === "animated" && editable;
                    return (
                      <div
                        key={i}
                        className={`compose-timeline__bar compose-timeline__bar--${seg.type}${draggable ? " compose-timeline__bar--draggable" : ""}`}
                        style={{
                          left: `${(display.start / maxTime) * 100}%`,
                          width: `${(display.duration / maxTime) * 100}%`,
                          background: color,
                        }}
                        title={`${seg.label} · ${display.start.toFixed(2)}s–${(display.start + display.duration).toFixed(2)}s`}
                        onMouseDown={
                          draggable ? (e) => startDrag(e, layer.id, seg.type, "move", seg.start, seg.duration) : undefined
                        }
                      >
                        {draggable ? (
                          <span
                            className="compose-timeline__bar-handle compose-timeline__bar-handle--left"
                            onMouseDown={(e) => startDrag(e, layer.id, seg.type, "resize-left", seg.start, seg.duration)}
                          />
                        ) : null}
                        <span className="compose-timeline__bar-label">{seg.label}</span>
                        {draggable ? (
                          <span
                            className="compose-timeline__bar-handle compose-timeline__bar-handle--right"
                            onMouseDown={(e) => startDrag(e, layer.id, seg.type, "resize-right", seg.start, seg.duration)}
                          />
                        ) : null}
                      </div>
                    );
                  })
                )}
                {isInfinite ? (
                  <div
                    className="compose-timeline__infinite"
                    style={{ left: `${(cycleDuration / maxTime) * 100}%`, borderColor: color, color }}
                    title="Repeats indefinitely"
                  >
                    <span className="compose-timeline__infinite-line" />
                    <span className="compose-timeline__infinite-mark">∞</span>
                  </div>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      </div>

      <p className="compose-timeline__hint">
        Click the ruler to inspect any moment; click a row to select that layer; drag a bar to move it, or its edges
        to resize.
      </p>
    </div>
  );
}